import {PurchaseRequest} from "@shared/models/user/purchase/PurchaseRequest";
import {ContentType, HttpMethod, HttpService} from "./HttpService";
import {
    CreateIndividualKedOfferRequest,
    CreateIndividualKedOfferResponse,
    GenerateProductAccessRequest,
    GenerateProductAccessResponse,
    IndividualKedOffer
} from "@shared/models/user/purchase/IndividualKedOffer";
import {
    GrantIndirectCourseAccessRequest,
    GrantIndirectCourseAccessResponse,
    ProductActivationCode
} from "@shared/models/user/purchase/KedAccessAfterWebinar";
import {PurchaseOption} from "@shared/models/user/purchase/PurchaseOption";
import {getSessionId} from "@shared/utils/TrackingUtil";
import {PublicUser} from "@shared/models/user/User";

// Re-export for backwards compatibility
export type PublicUserData = PublicUser;

export class PurchaseService {

    public static initWebinarPurchase(webinarId: string, source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_WEBINAR.toString(),
            name: "Webinar",
            email: "webinar@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'webinar'),
            webinarId: webinarId,
            successRedirectPath: `/webinar/zakup/sukces/${webinarId}`,
            errorRedirectPath: `/webinar/zakup/blad/${webinarId}`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initWebinarBundlePurchase(webinarId: string | undefined, source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_ZN_BUNDLE.toString(),
            name: "Webinar Bundle",
            email: "webinar-bundle@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'webinar-bundle'),
            webinarId: webinarId,
            successRedirectPath: `/webinar/zakup/sukces/${webinarId}`,
            errorRedirectPath: `/webinar/zakup/blad/${webinarId}`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initNewsletterPurchase(webinarId: string | undefined, source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_NEWSLETTER.toString(),
            name: "",
            email: "newsletter@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'newsletter'),
            successRedirectPath: `/newsletter/kurs-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/newsletter/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId(),
            webinarId: webinarId
        });
    }

    public static initShortFormSalesPageKedPurchase(webinarId: string | undefined, source: string | null): Promise<string> {
        const purchaseRequest: any = {
            purchaseOptionName: PurchaseOption.COURSE_KED_NEWSLETTER.toString(),
            name: "",
            email: "short-form@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'short-form'),
            successRedirectPath: `/newsletter/zakup/kurs-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/newsletter/zakup/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        };

        if (webinarId) {
            purchaseRequest.webinarId = webinarId;
        }

        return PurchaseService.initPurchase(purchaseRequest);
    }

    public static initShortFormSalesPageBundlePurchase(webinarId: string | undefined, source: string | null): Promise<string> {
        const purchaseRequest: any = {
            purchaseOptionName: PurchaseOption.COURSE_KED_ZN_BUNDLE.toString(),
            name: "",
            email: "short-form-bundle@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'short-form-bundle'),
            successRedirectPath: `/newsletter/zakup/kurs-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/newsletter/zakup/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        };

        if (webinarId) {
            purchaseRequest.webinarId = webinarId;
        }

        return PurchaseService.initPurchase(purchaseRequest);
    }

    public static initRadsPurchase(source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_RADS.toString(),
            name: "",
            email: "rads@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'rads'),
            successRedirectPath: `/rads/kurs-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/rads/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initWebinarSalesPagePurchase(
        source: string | null,
        referralCode?: string | null,
        // Optional override — caller passes a different PurchaseOption key for webinars whose
        // post-webinar sales page should resolve to a different product (e.g. webinar-25-04 → V3_ZN).
        // When omitted, falls back to the original COURSE_KED_WEBINAR_SALES_PAGE.
        purchaseOptionName?: string,
    ): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: purchaseOptionName ?? PurchaseOption.COURSE_KED_WEBINAR_SALES_PAGE.toString(),
            name: "",
            email: "webinar-sp@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'webinar-sp'),
            successRedirectPath: `/webinar/kurs-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/webinar/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId(),
            referralCode: referralCode || undefined
        });
    }

    public static initKedV2ReferralPurchase(referralCode: string): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_V2_REFERRAL.toString(),
            name: "",
            email: "referral@produkacja.pl",
            source: 'referral',
            successRedirectPath: `/polecenie/kurs-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/polecenie/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId(),
            referralCode: referralCode
        });
    }

    public static initKedFromPedSalesPagePurchase(source: string | null, email: string, name: string): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_FROM_PED_SALES_PAGE.toString(),
            name: name,
            email: email,
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'ped'),
            successRedirectPath: `/produkt`,
            errorRedirectPath: `/produkt/zakup/kurs-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initKedDiscountedPurchase(source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_KED_DISCOUNTED.toString(),
            name: "",
            email: "poczatek-roku-szkolnego@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'newsletter'),
            successRedirectPath: `/newsletter/poczatek-roku-szkolnego/sukces`,
            errorRedirectPath: `/newsletter/poczatek-roku-szkolnego/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initBlackFridayPurchase(source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.BLACKFRIDAY_SPECIAL_OFFER.toString(),
            name: "",
            email: "black-friday@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'newsletter'),
            successRedirectPath: `/newsletter/cyber-monday/sukces`,
            errorRedirectPath: `/newsletter/cyber-monday/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initChristmasPurchase(source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.CHRISTMAS_SPECIAL_OFFER.toString(),
            name: "",
            email: "christmas@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'newsletter'),
            successRedirectPath: `/newsletter/swieta/sukces`,
            errorRedirectPath: `/newsletter/swieta/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initPedPurchase(source: string | null): Promise<string> {
        return PurchaseService.initPurchase({
            purchaseOptionName: PurchaseOption.COURSE_PED.toString(),
            name: "Ped",
            email: "ped@produkacja.pl",
            source: source ?? (localStorage.getItem('firstJoinedFrom') ?? 'ped'),
            successRedirectPath: `/newsletter/podstawy-efektywnego-dzialania/sukces`,
            errorRedirectPath: `/newsletter/podstawy-efektywnego-dzialania/blad`,
            eventUrl: window.location.href,
            sessionId: getSessionId()
        });
    }

    public static initPurchase(purchaseRequest: PurchaseRequest): Promise<string> {
        return HttpService.sendRequest<string>(
            HttpMethod.POST,
            `/api/purchase/init`,
            JSON.stringify(purchaseRequest),
            ContentType.JSON
        );
    }

    public static getIndividualKedOffer(id: string): Promise<IndividualKedOffer> {
        return HttpService.sendRequest<IndividualKedOffer>(
            HttpMethod.GET,
            `/api/individual-ked-offer/${id}`,
        );
    }

    public static createIndividualKedOffer(createOfferRequest: CreateIndividualKedOfferRequest): Promise<CreateIndividualKedOfferResponse> {
        return HttpService.sendRequest<CreateIndividualKedOfferResponse>(
            HttpMethod.POST,
            `/api/individual-ked-offer`,
            JSON.stringify(createOfferRequest),
            ContentType.JSON
        );
    }

    public static generateProductAccess(request: GenerateProductAccessRequest): Promise<GenerateProductAccessResponse> {
        return HttpService.sendRequest<GenerateProductAccessResponse>(
            HttpMethod.POST,
            `/api/individual-ked-offer`,
            JSON.stringify(request),
            ContentType.JSON
        );
    }

    public static grantIndirectCourseAccess(grantCourseAccess: GrantIndirectCourseAccessRequest): Promise<GrantIndirectCourseAccessResponse> {
        return HttpService.sendRequest<GrantIndirectCourseAccessResponse>(
            HttpMethod.POST,
            `/api/activate-course-access`,
            JSON.stringify(grantCourseAccess),
            ContentType.JSON
        );
    }

    public static getCourseActivationCode(id: string): Promise<ProductActivationCode> {
        return HttpService.sendRequest<ProductActivationCode>(
            HttpMethod.GET,
            `/api/course-activation-code/${id}`,
        );
    }

    public static initStripeCheckout(purchaseRequest: PurchaseRequest): Promise<{
        checkoutUrl: string,
        sessionId: string
    }> {
        return HttpService.sendRequest<{ checkoutUrl: string, sessionId: string }>(
            HttpMethod.POST,
            `/api/purchase/stripe/init`,
            JSON.stringify(purchaseRequest),
            ContentType.JSON
        );
    }

    public static createCustomerPortalSession(): Promise<{ portalUrl: string }> {
        return HttpService.sendRequest<{ portalUrl: string }>(
            HttpMethod.POST,
            `/api/stripe/customer-portal`,
            undefined,
            ContentType.JSON
        );
    }

    /**
     * Initialize add-children upsell transaction via TPAY
     * Returns a redirect URL (string) to the TPAY payment page
     *
     * Can be called with either:
     * - code + additionalChildren (from aktywuj-kurs page where we have the code)
     * - transactionId + additionalChildren (from thank-you page where backend resolves the code)
     */
    public static initAddChildrenTransaction(request: {
        code?: string,
        transactionId?: string,
        additionalChildren: number,
        email: string,
        name: string,
        source: string,
        successRedirectPath?: string,
        errorRedirectPath?: string
    }): Promise<string> {
        return HttpService.sendRequest(
            HttpMethod.POST,
            `/api/purchase/init`,
            JSON.stringify({
                purchaseOptionName: 'ADD_CHILDREN_UPSELL',
                email: request.email,
                name: request.name,
                source: request.source,
                code: request.code,
                originalTransactionId: request.transactionId,
                additionalChildren: request.additionalChildren,
                successRedirectPath: request.successRedirectPath,
                errorRedirectPath: request.errorRedirectPath,
                sessionId: getSessionId()
            }),
            ContentType.JSON
        );
    }

    /**
     * Initialize add-children upsell transaction via Stripe
     * Returns a checkout URL and session ID
     */
    public static initAddChildrenStripeTransaction(request: {
        code?: string,
        transactionId?: string,
        additionalChildren: number,
        email: string,
        name: string,
        source: string,
        successRedirectPath?: string,
        errorRedirectPath?: string
    }): Promise<{ checkoutUrl: string, sessionId: string }> {
        return HttpService.sendRequest(
            HttpMethod.POST,
            `/api/purchase/stripe/init`,
            JSON.stringify({
                purchaseOptionName: 'ADD_CHILDREN_UPSELL',
                email: request.email,
                name: request.name,
                source: request.source,
                code: request.code,
                originalTransactionId: request.transactionId,
                additionalChildren: request.additionalChildren,
                successRedirectPath: request.successRedirectPath,
                errorRedirectPath: request.errorRedirectPath,
                sessionId: getSessionId()
            }),
            ContentType.JSON
        );
    }

    /**
     * Initialize platform upsell transaction via TPAY
     * Returns a redirect URL (string) to the TPAY payment page
     */
    public static initPlatformUpsellTransaction(request: {
        code: string,
        email: string,
        name: string,
        source: string,
        successRedirectPath?: string,
        errorRedirectPath?: string
    }): Promise<string> {
        return HttpService.sendRequest(
            HttpMethod.POST,
            `/api/purchase/init`,
            JSON.stringify({
                purchaseOptionName: 'PLATFORM_UPSELL',
                email: request.email,
                name: request.name,
                source: request.source,
                code: request.code,
                successRedirectPath: request.successRedirectPath,
                errorRedirectPath: request.errorRedirectPath,
                sessionId: getSessionId()
            }),
            ContentType.JSON
        );
    }
}